/**
 * APP ROUTER - CHATBOT IMÓVEIS
 * =============================
 * Rotas simplificadas e organizadas por domínio
 * Navegação otimizada: máximo 2-3 cliques para funções principais
 */

import { Suspense, useState, useEffect } from 'react';
import { Switch, Route, useLocation } from 'wouter';
import { queryClient } from './shared/lib/queryClient';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './shared/components/ui/toaster';
import { TooltipProvider } from './shared/components/ui/tooltip';
import { ThemeProvider } from './shared/components/ThemeProvider';
import { GlobalSettingsProvider } from './contexts/GlobalSettingsProvider';
import { lazyWithRetry, RouteLoader } from './shared/components/LazyRoute';
import WidgetSaaS from './components/WidgetSaaS';
import { FirebaseAuthProvider } from './contexts/FirebaseAuthContext';
import { AuthGuard } from './components/auth/AuthGuard';
import { DashboardRoute } from './components/nav';
import { PrivacyConsent } from './components/privacy/PrivacyConsent';
import { InstallPrompt } from './components/p-w-a/InstallPrompt';
import { privacyService } from './services/privacy';
import { secureLogger } from './utils/secure-logger';
import Header from './shared/components/layout/Header';
import { LanguageProvider } from './contexts/LanguageContext';
import { LeadsProvider } from './contexts/LeadsContext';
import './core/i18n/i18n';
import { initTracking, trackPageView } from './utils/analytics-tracker';
import { HelmetProvider } from 'react-helmet-async';
import { ErrorBoundary } from './components/common/ErrorBoundary';

// ====================================
// PÁGINAS PÚBLICAS (Lazy Loading)
// ====================================
const HomePage = lazyWithRetry(() => import('./pages/Home'));
const About = lazyWithRetry(() => import('./pages/About'));
const Features = lazyWithRetry(() => import('./pages/Features'));
const TermsOfService = lazyWithRetry(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazyWithRetry(() => import('./pages/PrivacyPolicy'));
const LiveDemo = lazyWithRetry(() => import('./pages/LiveDemo'));
const Downloads = lazyWithRetry(() => import('./pages/Downloads'));
const Help = lazyWithRetry(() => import('./pages/Help'));
const Brand = lazyWithRetry(() => import('./pages/Brand'));
const SentryTest = lazyWithRetry(() => import('./pages/SentryTest'));
import LoadingScreen from './components/LoadingScreen';
const NotFound = lazyWithRetry(() => import('./pages/NotFound'));

// ====================================
// AUTENTICAÇÃO
// ====================================
const LoginOptimized = lazyWithRetry(() => import('./pages/LoginOptimized'));
const Login2 = lazyWithRetry(() => import('./pages/Login2'));
const RedirectToLogin = lazyWithRetry(() => import('./pages/RedirectToLogin'));
const ActivateAccountPage = lazyWithRetry(() => import('./pages/ActivateAccountPage'));
const OnboardingProdutos = lazyWithRetry(() => import('./pages/OnboardingProdutos'));
const EmDesenvolvimento = lazyWithRetry(() => import('./pages/EmDesenvolvimento'));
const AdminDashboard = lazyWithRetry(() => import('./pages/AdminDashboard'));
const AdminLeadsPage = lazyWithRetry(() => import('./pages/AdminLeadsPage'));

// Admin Pages (novo painel completo)
const AdminDashboardPage = lazyWithRetry(() => import('./pages/admin/AdminDashboardPage'));
const AdminLeadsWidgetPage = lazyWithRetry(() => import('./pages/admin/AdminLeadsWidgetPage'));
const AdminLeadsOrganizadorPage = lazyWithRetry(() => import('./pages/admin/AdminLeadsOrganizadorPage'));
const AdminUsuariosPage = lazyWithRetry(() => import('./pages/admin/AdminUsuariosPage'));
const AdminAssinaturasPage = lazyWithRetry(() => import('./pages/admin/AdminAssinaturasPage'));
const AdminConversasPage = lazyWithRetry(() => import('./pages/admin/AdminConversasPage'));
const AdminMetricasPage = lazyWithRetry(() => import('./pages/admin/AdminMetricasPage'));
const AdminKnowledgeBasePage = lazyWithRetry(() => import('./pages/admin/AdminKnowledgeBasePage'));
const AdminConfiguracoesPage = lazyWithRetry(() => import('./pages/admin/AdminConfiguracoesPage'));

// ====================================
// DASHBOARD & NAVEGAÇÃO PRINCIPAL
// ====================================
// NOVO: Dashboard Central (Centro de Comando)
const DashboardCentral = lazyWithRetry(() => import('./pages/DashboardCentral'));
// ANTIGO: Dashboard legado (comentado temporariamente)
// const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
// DESCONECTADO TEMPORARIAMENTE - REFACTOR FRONTEND PÓS-LOGIN (2025-11-08)
const ProfilePage = lazyWithRetry(() => import('./pages/ProfilePage'));
const SettingsPage = lazyWithRetry(() => import('./pages/SettingsPage'));
const WhatsAppSettingsPage = lazyWithRetry(() => import('./pages/WhatsAppSettingsPage'));
// const NotificationsPage = lazyWithRetry(() => import('./pages/NotificationsPage'));

// ====================================
// ORGANIZADOR DE CLIENTES (Produto Gratuito)
// ====================================
const OrganizadorClientesDashboard = lazyWithRetry(
  () => import('./pages/OrganizadorClientesDashboard')
);
const NovoLeadForm = lazyWithRetry(() => import('./pages/NovoLeadForm'));
const LeadDetailsPageAdvanced = lazyWithRetry(() => import('./pages/LeadDetailsPageAdvanced'));
// const WhatsAppView = lazyWithRetry(() => import('./components/leads/WhatsAppView'));
const SDRBot = lazyWithRetry(() => import('./pages/SDRBot'));
const SDRConfigPage = lazyWithRetry(() => import('./pages/SDRConfigPage'));
const ZecaTestView = lazyWithRetry(() => import('./pages/ZecaTestView'));
// const MeuApiToken = lazyWithRetry(() => import('./pages/MeuApiToken'));
// const OrganizadorOnboarding = lazyWithRetry(() => import('./pages/OrganizadorOnboarding'));
// const SDRAnalyticsView = lazyWithRetry(() => import('./components/sdr/SDRAnalyticsView'));
// const SDRTemplatesView = lazyWithRetry(() => import('./components/sdr/SDRTemplatesView'));
// const SDRFollowUpsView = lazyWithRetry(() => import('./components/sdr/SDRFollowUpsView'));
// const ChatPage = lazyWithRetry(() => import('./pages/ChatPage'));
const AgendaView = lazyWithRetry(() => import('./components/leads/AgendaView'));
const KanbanPage = lazyWithRetry(() => import('./pages/dashboard/KanbanPage'));
const LeadsQuentes = lazyWithRetry(() => import('./pages/leads/LeadsQuentes'));
const LeadsMornos = lazyWithRetry(() => import('./pages/leads/LeadsMornos'));
const LeadsFrios = lazyWithRetry(() => import('./pages/leads/LeadsFrios'));
const LeadsPorEtapa = lazyWithRetry(() => import('./pages/leads/LeadsPorEtapa'));

// ====================================
// PRODUTOS DASHBOARD (BETA)
// ====================================
const GestorVendasProPage = lazyWithRetry(() => import('./pages/dashboard/GestorVendasProPage'));
const AtendenteVirtualPage = lazyWithRetry(() => import('./pages/dashboard/AtendenteVirtualPage'));
const ContratosDigitaisPage = lazyWithRetry(
  () => import('./pages/dashboard/ContratosDigitaisPage')
);
const VitrineOnlinePage = lazyWithRetry(() => import('./pages/dashboard/VitrineOnlinePage'));
const SecretariaDigitalPage = lazyWithRetry(
  () => import('./pages/dashboard/SecretariaDigitalPage')
);
const GestaoProfissionalPage = lazyWithRetry(
  () => import('./pages/dashboard/GestaoProfissionalPage')
);
const SolucaoPersonalizadaPage = lazyWithRetry(
  () => import('./pages/dashboard/SolucaoPersonalizadaPage')
);

// ====================================
// ANALYTICS & IA
// ====================================
const LeadInsightsPage = lazyWithRetry(() => import('./pages/dashboard/LeadInsightsPage'));
const AIAgentsPage = lazyWithRetry(() => import('./pages/dashboard/AIAgentsPage'));

// ====================================
// MARKETING AUTOMATION
// ====================================
const MarketingCampaignsPage = lazyWithRetry(() => import('./pages/MarketingCampaignsPage'));

// ====================================
// CHECKOUT & PAGAMENTOS
// ====================================
const CheckoutPage = lazyWithRetry(() => import('./pages/CheckoutPage'));
const CheckoutSuccessPage = lazyWithRetry(() => import('./pages/CheckoutSuccessPage'));
const CheckoutSuccessImplementationPage = lazyWithRetry(() => import('./pages/CheckoutSuccessImplementationPage'));
const UpgradePage = lazyWithRetry(() => import('./pages/dashboard/UpgradePage'));
const SubscriptionCheckout = lazyWithRetry(() => import('./pages/checkout/SubscriptionCheckout'));

// ====================================
// IMPLEMENTACOES CUSTOMIZADAS
// ====================================
const ImplementacoesListPage = lazyWithRetry(() => import('./pages/dashboard/ImplementacoesListPage'));
const ImplementacaoDetailPage = lazyWithRetry(() => import('./pages/dashboard/ImplementacaoDetailPage'));

// ====================================
// PÁGINAS DE MARKETING
// ====================================
const PricingOptimized = lazyWithRetry(() => import('./pages/PricingOptimized'));
const Services = lazyWithRetry(() => import('./domains/marketing/pages/services'));
const Benefits = lazyWithRetry(() => import('./domains/marketing/pages/benefits'));
const Contact = lazyWithRetry(() => import('./domains/marketing/pages/contact'));
const CombosPage = lazyWithRetry(() => import('./pages/BundlesPage'));

// Páginas de Produtos (Landing Pages)
const OrganizadorClientesOtimizado = lazyWithRetry(
  () => import('./domains/marketing/pages/produtos/organizador-clientes-otimizado')
);
const AtendenteVirtualOtimizado = lazyWithRetry(
  () => import('./domains/marketing/pages/produtos/atendente-virtual-otimizado')
);
const SecretariaDigitalOtimizada = lazyWithRetry(
  () => import('./domains/marketing/pages/produtos/secretaria-digital-otimizada')
);
const SecretariaDigitalInteresse = lazyWithRetry(
  () => import('./domains/marketing/pages/produtos/secretaria-digital-interesse')
);
const ContratosDigitaisOtimizado = lazyWithRetry(
  () => import('./domains/marketing/pages/produtos/contratos-digitais-otimizado')
);
const VitrineOnlineOtimizada = lazyWithRetry(
  () => import('./domains/marketing/pages/produtos/vitrine-online-otimizada')
);
const GestaoProfissional = lazyWithRetry(
  () => import('./domains/marketing/pages/produtos/gestao-profissional')
);
const SolucaoPersonalizada = lazyWithRetry(
  () => import('./domains/marketing/pages/produtos/solucao-personalizada')
);
const GestorVendasProOtimizado = lazyWithRetry(
  () => import('./domains/marketing/pages/produtos/gestor-vendas-pro-otimizado')
);
const MonitoramentoReputacaoOtimizado = lazyWithRetry(
  () => import('./domains/marketing/pages/produtos/monitoramento-reputacao-otimizado')
);
const ProtecaoFraudesOtimizado = lazyWithRetry(
  () => import('./domains/marketing/pages/produtos/protecao-fraudes-otimizado')
);
const AnaliseRiscoJuridicoOtimizado = lazyWithRetry(
  () => import('./domains/marketing/pages/produtos/analise-risco-juridico-otimizado')
);
const DueDiligenceOtimizado = lazyWithRetry(
  () => import('./domains/marketing/pages/produtos/due-diligence-otimizado')
);

// Combos
const ComboEssencial = lazyWithRetry(
  () => import('./domains/marketing/pages/combos/combo-essencial')
);
const ComboProfissional = lazyWithRetry(
  () => import('./domains/marketing/pages/combos/combo-profissional')
);
const ComboAvancado = lazyWithRetry(
  () => import('./domains/marketing/pages/combos/combo-avancado')
);
const ComboEnterprise = lazyWithRetry(
  () => import('./domains/marketing/pages/combos/combo-enterprise')
);

// Páginas Legais
const FAQ = lazyWithRetry(() => import('./domains/marketing/pages/legal/faq'));
const Termos = lazyWithRetry(() => import('./domains/marketing/pages/legal/termos'));
const Privacidade = lazyWithRetry(() => import('./domains/marketing/pages/legal/privacidade'));
const LGPD = lazyWithRetry(() => import('./domains/marketing/pages/legal/lgpd'));

// ====================================
// DOWNLOADS
// ====================================
const MobileApp = lazyWithRetry(() => import('./pages/downloads/MobileApp.jsx'));
const Brochures = lazyWithRetry(() => import('./pages/downloads/Brochures.jsx'));
const Guides = lazyWithRetry(() => import('./pages/downloads/Guides.jsx'));
const Contracts = lazyWithRetry(() => import('./pages/downloads/Contracts.jsx'));

// ====================================
// ADMIN & DESENVOLVIMENTO
// ====================================
const AdminMigrations = lazyWithRetry(() => import('./pages/AdminMigrations'));
const InitializeDatabase = lazyWithRetry(() => import('./pages/InitializeDatabase'));
const Demo = lazyWithRetry(() => import('./pages/Demo'));
const AreaConfigPage = lazyWithRetry(() => import('./pages/AreaConfigPage'));
const UpgradeTriggersDemo = lazyWithRetry(() => import('./pages/UpgradeTriggersDemo'));
const ChatInterface = lazyWithRetry(() => import('./pages/ChatInterface'));

// ====================================
// SCROLL TO TOP ON ROUTE CHANGE
// ====================================
function ScrollToTop() {
  const [location] = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location]);

  return null;
}

// ====================================
// ROUTER
// ====================================
function Router() {
  return (
    <Suspense fallback={<RouteLoader />}>
      <ScrollToTop />
      <Switch>
        {/* ================== */}
        {/* HOME & MARKETING   */}
        {/* ================== */}
        <Route path='/' component={HomePage} />
        <Route path='/about' component={About} />
        <Route path='/features' component={Features} />
        <Route path='/services' component={Services} />
        <Route path='/benefits' component={Benefits} />
        <Route path='/contact' component={Contact} />
        <Route path='/help' component={Help} />
        <Route path='/live-demo' component={LiveDemo} />
        <Route path='/brand' component={Brand} />
        <Route path='/sentry-test' component={SentryTest} />

        {/* Pricing */}
        <Route path='/pricing'>{() => <PricingOptimized />}</Route>
        <Route path='/precos'>{() => <PricingOptimized />}</Route>

        {/* ================== */}
        {/* AUTENTICAÇÃO       */}
        {/* ================== */}
        <Route path='/login'>{() => <Login2 />}</Route>
        <Route path='/login-old'>{() => <LoginOptimized />}</Route>
        <Route path='/register'>{() => <RedirectToLogin />}</Route>
        <Route path='/activate-account'>{() => <ActivateAccountPage />}</Route>
        <Route path='/onboarding-produtos'>{() => <OnboardingProdutos />}</Route>

        {/* ============================= */}
        {/* EM DESENVOLVIMENTO (EARLY ACCESS) */}
        {/* ============================= */}
        <Route path='/em-desenvolvimento'>
          {() => (
            <AuthGuard requireAuth={true}>
              <EmDesenvolvimento />
            </AuthGuard>
          )}
        </Route>

        {/* ================== */}
        {/* ADMIN DASHBOARD    */}
        {/* ================== */}
        <Route path='/admin'>
          {() => (
            <AuthGuard requireAuth={true}>
              <AdminDashboard />
            </AuthGuard>
          )}
        </Route>

        {/* ============================================== */}
        {/* DASHBOARD CENTRAL - CENTRO DE COMANDO       */}
        {/* ============================================== */}
        {/*
         * ROTEAMENTO DASHBOARD
         * ====================
         * Todas as rotas /dashboard/* são protegidas por AuthGuard.
         * Implementação ativa: DashboardCentral.tsx
         *
         * IMPORTANTE: Rotas públicas (landing, login) NÃO devem renderizar
         * dentro do contexto /dashboard/*.
         *
         * Estrutura:
         * - /dashboard               → DashboardCentral (Centro de Comando)
         * - /dashboard/profile       → ProfilePage
         * - /dashboard/settings      → SettingsPage
         *
         * ORGANIZADOR DE CLIENTES (GRATUITO):
         * - /dashboard/organizador-clientes → OrganizadorClientesDashboard
         * - /dashboard/kanban        → KanbanPage
         * - /dashboard/sdr           → SDRBot (Zeca - limite 100 usos)
         * - /dashboard/leads/agenda  → AgendaView
         *
         * PRODUTOS PREMIUM (8 MÓDULOS):
         * - /dashboard/secretaria-digital (R$ 800)     → SecretariaDigitalPage
         * - /dashboard/gestor-vendas-pro (R$ 800)      → GestorVendasProPage
         * - /dashboard/vitrine-online (R$ 1.200)       → VitrineOnlinePage
         * - /dashboard/contratos-digitais (R$ 1.800)   → ContratosDigitaisPage
         * - /dashboard/atendente-virtual (R$ 2.200)    → AtendenteVirtualPage
         * - /dashboard/gestao-profissional (R$ 3.500)  → GestaoProfissionalPage
         * - /dashboard/solucao-personalizada (R$ 15k)  → SolucaoPersonalizadaPage
         */}
        <Route path='/dashboard'>
          {() => (
            <DashboardRoute>
              <DashboardCentral />
            </DashboardRoute>
          )}
        </Route>

        <Route path='/dashboard/profile'>
          {() => (
            <DashboardRoute>
              <ProfilePage />
            </DashboardRoute>
          )}
        </Route>

        <Route path='/dashboard/settings'>
          {() => (
            <DashboardRoute>
              <SettingsPage />
            </DashboardRoute>
          )}
        </Route>

        <Route path='/dashboard/whatsapp-settings'>
          {() => (
            <DashboardRoute>
              <WhatsAppSettingsPage />
            </DashboardRoute>
          )}
        </Route>

        {/* <Route path='/dashboard/notifications'>
          {() => (
            <AuthGuard requireAuth={true}>
              <NotificationsPage />
            </AuthGuard>
          )}
        </Route> */}

        {/* ================================ */}
        {/* ORGANIZADOR DE CLIENTES (FREE)  */}
        {/* ================================ */}

        {/* Agenda de Leads */}
        <Route path='/dashboard/leads/agenda'>
          {() => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <AgendaView />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        {/* Kanban de Leads */}
        <Route path='/dashboard/kanban'>
          {() => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <KanbanPage />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        {/* Marketing Campaigns - Automação Meta Ads com IA */}
        <Route path='/dashboard/marketing-campaigns'>
          {() => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <MarketingCampaignsPage />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        {/* SDR com IA */}
        <Route path='/dashboard/sdr'>
          {() => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <SDRConfigPage />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        {/* SDR Config - Rota alternativa */}
        <Route path='/dashboard/sdr-config'>
          {() => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <SDRConfigPage />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        {/* Zeca - Testes SDR */}
        <Route path='/dashboard/zeca-testes'>
          {() => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <ZecaTestView />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        {/* Dashboard Principal do Organizador */}
        <Route path='/dashboard/organizador-clientes'>
          {() => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <OrganizadorClientesDashboard />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        {/* Novo Lead */}
        <Route path='/dashboard/organizador-clientes/novo-lead'>
          {() => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <NovoLeadForm />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        {/* Detalhes do Lead */}
        <Route path='/dashboard/organizador-clientes/leads/:leadId'>
          {params => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <LeadDetailsPageAdvanced leadId={(params as any)?.leadId ?? ''} />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        {/* ================================================ */}
        {/* PRODUTOS DASHBOARD - 8 MÓDULOS                  */}
        {/* ================================================ */}

        {/* Secretária Digital (R$ 800/mês) - Zeca ilimitado */}
        <Route path='/dashboard/secretaria-digital'>
          {() => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <SecretariaDigitalPage />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        {/* Gestor de Vendas Pro (R$ 800/mês) */}
        <Route path='/dashboard/gestor-vendas-pro'>
          {() => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <GestorVendasProPage />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        {/* Atendente Virtual (R$ 2.200/mês) */}
        <Route path='/dashboard/atendente-virtual'>
          {() => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <AtendenteVirtualPage />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        {/* Contratos Digitais (R$ 1.800/mês) */}
        <Route path='/dashboard/contratos-digitais'>
          {() => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <ContratosDigitaisPage />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        {/* Vitrine Online (R$ 1.200/mês) */}
        <Route path='/dashboard/vitrine-online'>
          {() => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <VitrineOnlinePage />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        {/* Gestão Profissional (R$ 3.500/mês) */}
        <Route path='/dashboard/gestao-profissional'>
          {() => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <GestaoProfissionalPage />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        {/* Solução Personalizada (R$ 15.000/mês) */}
        <Route path='/dashboard/solucao-personalizada'>
          {() => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <SolucaoPersonalizadaPage />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        {/* Upgrade - Página de assinatura de produtos/combos */}
        <Route path='/dashboard/upgrade'>
          {() => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <UpgradePage />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        {/* Checkout - Pagamento via Stripe */}
        <Route path='/checkout/:productId?'>
          {params => (
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingScreen />}>
                <CheckoutPage />
              </Suspense>
            </AuthGuard>
          )}
        </Route>

        {/* Checkout Success - Confirmação de pagamento (PUBLIC - guest checkout allowed) */}
        <Route path='/checkout/success'>
          {() => (
            <Suspense fallback={<LoadingScreen />}>
              <CheckoutSuccessPage />
            </Suspense>
          )}
        </Route>

        {/* Checkout Success - Implementação Customizada (PUBLIC - guest checkout allowed) */}
        <Route path='/checkout/success/implementation'>
          {() => (
            <Suspense fallback={<LoadingScreen />}>
              <CheckoutSuccessImplementationPage />
            </Suspense>
          )}
        </Route>

        {/* Subscription Checkout - Nova rota para assinaturas Stripe (REQUER LOGIN) */}
        <Route path='/subscription/checkout/:productId'>
          {params => (
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingScreen />}>
                <SubscriptionCheckout />
              </Suspense>
            </AuthGuard>
          )}
        </Route>

        {/* ================================ */}
        {/* IMPLEMENTACOES CUSTOMIZADAS     */}
        {/* ================================ */}

        {/* Lista de Implementações */}
        <Route path='/dashboard/implementacoes'>
          {() => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <ImplementacoesListPage />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        {/* Detalhes da Implementação */}
        <Route path='/dashboard/implementacoes/:id'>
          {params => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <ImplementacaoDetailPage />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        {/* Analytics - Insights de Leads e Métricas */}
        <Route path='/dashboard/analytics'>
          {() => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <LeadInsightsPage />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        {/* IA Premium - Agentes de Inteligência Artificial */}
        <Route path='/dashboard/ai-assistant'>
          {() => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <AIAgentsPage />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        {/* ================================ */}
        {/* VIEWS FILTRADAS DE LEADS        */}
        {/* ================================ */}

        {/* Leads por Temperatura */}
        <Route path='/dashboard/leads/quentes'>
          {() => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <LeadsQuentes />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        <Route path='/dashboard/leads/mornos'>
          {() => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <LeadsMornos />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        <Route path='/dashboard/leads/frios'>
          {() => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <LeadsFrios />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        {/* Leads por Etapa */}
        <Route path='/dashboard/leads/etapa/:stageKey'>
          {params => (
            <DashboardRoute>
              <Suspense fallback={<LoadingScreen />}>
                <LeadsPorEtapa stageKey={(params as any)?.stageKey} />
              </Suspense>
            </DashboardRoute>
          )}
        </Route>

        {/* ================== */}
        {/* LANDING PAGES      */}
        {/* ================== */}

        {/* Produtos */}
        <Route path='/produto/organizador-clientes'>{() => <OrganizadorClientesOtimizado />}</Route>
        <Route path='/produto/atendente-virtual'>{() => <AtendenteVirtualOtimizado />}</Route>
        <Route path='/produto/secretaria-digital'>{() => <SecretariaDigitalOtimizada />}</Route>
        <Route path='/secretaria-digital/interesse'>{() => <SecretariaDigitalInteresse />}</Route>
        <Route path='/produto/contratos-digitais'>{() => <ContratosDigitaisOtimizado />}</Route>
        <Route path='/produto/vitrine-online'>{() => <VitrineOnlineOtimizada />}</Route>
        <Route path='/produto/gestao-profissional'>{() => <GestaoProfissional />}</Route>
        <Route path='/produto/solucao-personalizada'>{() => <SolucaoPersonalizada />}</Route>
        <Route path='/produto/gestor-vendas-pro'>{() => <GestorVendasProOtimizado />}</Route>
        <Route path='/produto/monitoramento-reputacao'>
          {() => <MonitoramentoReputacaoOtimizado />}
        </Route>
        <Route path='/produto/protecao-fraudes'>{() => <ProtecaoFraudesOtimizado />}</Route>
        <Route path='/produto/analise-risco-juridico'>
          {() => <AnaliseRiscoJuridicoOtimizado />}
        </Route>
        <Route path='/produto/due-diligence'>{() => <DueDiligenceOtimizado />}</Route>

        {/* Combos */}
        <Route path='/combo/essencial'>{() => <ComboEssencial />}</Route>
        <Route path='/combo/profissional'>{() => <ComboProfissional />}</Route>
        <Route path='/combo/avancado'>{() => <ComboAvancado />}</Route>
        <Route path='/combo/enterprise'>{() => <ComboEnterprise />}</Route>
        <Route path='/combos' component={CombosPage} />

        {/* ================== */}
        {/* DOWNLOADS          */}
        {/* ================== */}
        <Route path='/downloads' component={Downloads} />
        <Route path='/downloads/app'>{() => <MobileApp />}</Route>
        <Route path='/downloads/brochures'>{() => <Brochures />}</Route>
        <Route path='/downloads/guides'>{() => <Guides />}</Route>
        <Route path='/downloads/contracts'>{() => <Contracts />}</Route>

        {/* ================== */}
        {/* LEGAL              */}
        {/* ================== */}
        <Route path='/terms-of-service' component={TermsOfService} />
        <Route path='/termos'>{() => <Termos />}</Route>
        <Route path='/privacy-policy' component={PrivacyPolicy} />
        <Route path='/privacidade'>{() => <Privacidade />}</Route>
        <Route path='/lgpd'>{() => <LGPD />}</Route>
        <Route path='/faq'>{() => <FAQ />}</Route>

        {/* ================== */}
        {/* ADMIN & DEV        */}
        {/* ================== */}
        {/* Novo Painel Admin Completo — requer autenticação */}
        <Route path='/admin'>{() => <AuthGuard requireAuth={true}><AdminDashboardPage /></AuthGuard>}</Route>
        <Route path='/admin/leads-widget'>{() => <AuthGuard requireAuth={true}><AdminLeadsWidgetPage /></AuthGuard>}</Route>
        <Route path='/admin/leads-organizador'>{() => <AuthGuard requireAuth={true}><AdminLeadsOrganizadorPage /></AuthGuard>}</Route>
        <Route path='/admin/usuarios'>{() => <AuthGuard requireAuth={true}><AdminUsuariosPage /></AuthGuard>}</Route>
        <Route path='/admin/assinaturas'>{() => <AuthGuard requireAuth={true}><AdminAssinaturasPage /></AuthGuard>}</Route>
        <Route path='/admin/conversas'>{() => <AuthGuard requireAuth={true}><AdminConversasPage /></AuthGuard>}</Route>
        <Route path='/admin/metricas'>{() => <AuthGuard requireAuth={true}><AdminMetricasPage /></AuthGuard>}</Route>
        <Route path='/admin/knowledge-base'>{() => <AuthGuard requireAuth={true}><AdminKnowledgeBasePage /></AuthGuard>}</Route>
        <Route path='/admin/configuracoes'>{() => <AuthGuard requireAuth={true}><AdminConfiguracoesPage /></AuthGuard>}</Route>

        {/* Legado - redireciona para novo */}
        <Route path='/admin/leads'>
          {() => <AuthGuard requireAuth={true}><AdminLeadsWidgetPage /></AuthGuard>}
        </Route>
        <Route path='/admin/migrations'>
          {() => (
            <AuthGuard requireAuth={true}>
              <AdminMigrations />
            </AuthGuard>
          )}
        </Route>
        <Route path='/admin/init-db'>
          {() => (
            <AuthGuard requireAuth={true}>
              <InitializeDatabase />
            </AuthGuard>
          )}
        </Route>
        <Route path='/demo' component={Demo} />
        <Route path='/demo-data/area-config'>
          {() => (
            <AuthGuard requireAuth={true}>
              <AreaConfigPage />
            </AuthGuard>
          )}
        </Route>
        <Route path='/upgrade-demo'>
          {() => (
            <AuthGuard requireAuth={true}>
              <UpgradeTriggersDemo />
            </AuthGuard>
          )}
        </Route>
        <Route path='/chat'>
          {() => (
            <AuthGuard requireAuth={true}>
              <ChatInterface />
            </AuthGuard>
          )}
        </Route>

        {/* ================== */}
        {/* 404 - NOT FOUND    */}
        {/* ================== */}
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

// ====================================
// APP PRINCIPAL
// ====================================
function App() {
  const [showConsent, setShowConsent] = useState(false);

  useEffect(() => {
    // Inicializar tracking
    initTracking();

    // Verificar consentimento de privacidade
    const hasConsent = privacyService.hasConsent();
    if (!hasConsent) {
      setShowConsent(true);
    }

    // Track page view
    trackPageView(window.location.pathname);
  }, []);

  const handleConsentUpdate = (consentData: any) => {
    // Salvar consentimento detalhado
    const hasAnyConsent =
      consentData.analytics || consentData.marketing || consentData.personalization;
    privacyService.setConsent(hasAnyConsent || consentData.essential);
    setShowConsent(false);
    secureLogger.info('Privacy consent updated', {
      analytics: consentData.analytics,
      marketing: consentData.marketing,
      personalization: consentData.personalization,
    });
  };

  return (
    <ErrorBoundary
      showDetails={import.meta.env.DEV}
      onError={(error, errorInfo) => {
        // TODO: Enviar para Sentry quando configurado
        console.error('Global error captured by ErrorBoundary:', error, errorInfo);
      }}
    >
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider defaultTheme='light' storageKey='chatbot-imoveis-theme'>
            <TooltipProvider>
              <FirebaseAuthProvider>
                <GlobalSettingsProvider>
                  <LanguageProvider>
                    <LeadsProvider>
                      <div className='min-h-screen bg-background font-sans antialiased'>
                        <Header />
                        <Router />
                        <WidgetSaaS position='bottom-right' />
                        <InstallPrompt />
                        <Toaster />
                        {showConsent && <PrivacyConsent onConsentUpdate={handleConsentUpdate} />}
                      </div>
                    </LeadsProvider>
                  </LanguageProvider>
                </GlobalSettingsProvider>
              </FirebaseAuthProvider>
            </TooltipProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
